import { extendTheme, HTMLChakraProps, ThemingProps } from '@chakra-ui/react';
import { CardComponent } from './additions/card/card';
import { badgeStyles } from './components/badge';
import { buttonStyles } from './components/button';
import { inputStyles } from './components/input';
import { linkStyles } from './components/link';
import { progressStyles } from './components/progress';
import { sliderStyles } from './components/slider';
import { switchStyles } from './components/switch';
import { textareaStyles } from './components/textarea';
import { breakpoints } from './foundations/breakpoints';
import { buildGlobalStyles } from './styles';
import { themePresets, PresetId } from './presets';
import { aesthetics, AestheticId } from './aesthetics';

/**
 * Build a Chakra theme by merging:
 *   1. An aesthetic's neutral surfaces + semantics + shadows (the "trim level")
 *   2. A preset's brand color palette + brand shadows (the "paint color")
 *
 * Component styles (buttons, inputs, cards, etc.) are shared across all combos.
 */
export function buildTheme(presetId: PresetId = 'default', aestheticId: AestheticId = 'classic') {
	const preset = themePresets[presetId] ?? themePresets.default;
	const aesthetic = aesthetics[aestheticId] ?? aesthetics.classic;

	// Build aesthetic-specific global styles (surfaces, semantics, shadows)
	const baseStyles = buildGlobalStyles(aesthetic);

	// Deep-merge preset colors on top of aesthetic base colors.
	// For non-classic aesthetics, the aesthetic owns neutral surfaces
	// (secondaryGray, navy dark tones) — presets only change brand/accent.
	const aestheticOwnsSurfaces = aestheticId !== 'classic';

	const mergedColors: Record<string, any> = { ...baseStyles.colors };
	for (const [key, overrides] of Object.entries(preset.colors)) {
		if (aestheticOwnsSurfaces && key === 'secondaryGray') {
			// Skip — aesthetic controls the page background
			continue;
		}
		if (aestheticOwnsSurfaces && key === 'navy') {
			// Only merge decorative navy (50–600), preserve aesthetic dark surfaces (700–900)
			const filtered: Record<string, string> = {};
			for (const [shade, val] of Object.entries(overrides)) {
				if (Number(shade) < 700) filtered[shade] = val;
			}
			mergedColors[key] = { ...(mergedColors[key] || {}), ...filtered };
			continue;
		}
		mergedColors[key] = { ...(mergedColors[key] || {}), ...overrides };
	}

	const mergedStyles = {
		...baseStyles,
		colors: mergedColors,
		shadows: { ...baseStyles.shadows, ...preset.shadows },
	};

	return extendTheme(
		{ breakpoints },
		mergedStyles,
		badgeStyles,
		buttonStyles,
		linkStyles,
		progressStyles,
		sliderStyles,
		inputStyles,
		textareaStyles,
		switchStyles,
		CardComponent,
	);
}

export default buildTheme('default', 'classic');

export interface CustomCardProps extends HTMLChakraProps<'div'>, ThemingProps { }
